'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { Cookie, ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type Lang = 'en' | 'ar' | 'fr';

const translations = {
  en: {
    dir: 'ltr' as const,
    back: 'Back to Home',
    badge: 'Cookie Policy',
    title: 'Cookie Policy',
    updated: 'Last updated: August 2026',
    sections: [
      {
        heading: '1. What Are Cookies',
        body: 'Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, keep you logged in, and understand how you use the site.',
      },
      {
        heading: '2. How We Use Cookies',
        body: 'WealthOS uses cookies to maintain your session (keep you logged in), remember your language and display preferences, and analyze how our platform is used to improve the experience.',
      },
      {
        heading: '3. Types of Cookies We Use',
        body: 'Necessary Cookies: Required for the website to function correctly. These cannot be disabled. They include session tokens and authentication cookies. Analytics Cookies: Help us understand how visitors interact with WealthOS. These are only set with your consent. Marketing Cookies: Used to deliver relevant content and measure campaign effectiveness. Only set with your explicit consent.',
      },
      {
        heading: '4. Third-Party Cookies',
        body: 'We may use third-party services such as analytics providers that set their own cookies. These are subject to the respective third-party privacy policies. We do not control these cookies.',
      },
      {
        heading: '5. Managing Cookies',
        body: 'You can manage your cookie preferences at any time using the cookie consent banner displayed when you first visit WealthOS. You can also control cookies through your browser settings. Note that disabling necessary cookies may affect the functionality of the platform.',
      },
      {
        heading: '6. Cookie Retention',
        body: 'Session cookies are deleted when you close your browser. Persistent cookies remain on your device for a set period or until you delete them. Authentication cookies typically expire after 7 days of inactivity.',
      },
      {
        heading: '7. Changes to This Policy',
        body: 'We may update this Cookie Policy from time to time. We will notify you of significant changes. Continued use of WealthOS after changes constitutes acceptance of the updated policy.',
      },
      {
        heading: '8. Contact',
        body: 'For questions about our use of cookies, please contact us through the Support page or email support@wealthos.app.',
      },
    ],
  },
  ar: {
    dir: 'rtl' as const,
    back: 'العودة للرئيسية',
    badge: 'سياسة ملفات تعريف الارتباط',
    title: 'سياسة ملفات تعريف الارتباط',
    updated: 'آخر تحديث: أغسطس 2026',
    sections: [
      {
        heading: '١. ما هي ملفات تعريف الارتباط',
        body: 'ملفات تعريف الارتباط هي ملفات نصية صغيرة تُخزَّن على جهازك عند زيارة موقع ويب. تساعد المواقع على تذكر تفضيلاتك والحفاظ على تسجيل دخولك وفهم كيفية استخدامك للموقع.',
      },
      {
        heading: '٢. كيف نستخدم ملفات تعريف الارتباط',
        body: 'يستخدم WealthOS ملفات تعريف الارتباط للحفاظ على جلستك (إبقائك مسجلاً الدخول)، وتذكر تفضيلات اللغة والعرض، وتحليل كيفية استخدام منصتنا لتحسين التجربة.',
      },
      {
        heading: '٣. أنواع ملفات تعريف الارتباط التي نستخدمها',
        body: 'الملفات الضرورية: مطلوبة لعمل الموقع بشكل صحيح ولا يمكن تعطيلها، وتشمل رموز الجلسة وملفات المصادقة. الملفات التحليلية: تساعدنا على فهم تفاعل الزوار مع WealthOS وتُعيَّن فقط بموافقتك. الملفات التسويقية: تُستخدم لتقديم محتوى ذي صلة وقياس فعالية الحملات، وتُعيَّن فقط بموافقتك الصريحة.',
      },
      {
        heading: '٤. ملفات تعريف الارتباط التابعة لجهات خارجية',
        body: 'قد نستخدم خدمات طرف ثالث مثل مزودي التحليلات الذين يضعون ملفات تعريف الارتباط الخاصة بهم. تخضع هذه الملفات لسياسات الخصوصية الخاصة بالطرف الثالث المعني. لا نتحكم في هذه الملفات.',
      },
      {
        heading: '٥. إدارة ملفات تعريف الارتباط',
        body: 'يمكنك إدارة تفضيلات ملفات تعريف الارتباط في أي وقت باستخدام لافتة الموافقة المعروضة عند زيارتك الأولى لـ WealthOS. يمكنك أيضاً التحكم في ملفات تعريف الارتباط من خلال إعدادات متصفحك. لاحظ أن تعطيل الملفات الضرورية قد يؤثر على وظائف المنصة.',
      },
      {
        heading: '٦. الاحتفاظ بملفات تعريف الارتباط',
        body: 'تُحذف ملفات تعريف الارتباط للجلسة عند إغلاق متصفحك. تبقى الملفات الدائمة على جهازك لفترة محددة أو حتى تحذفها. تنتهي صلاحية ملفات المصادقة عادةً بعد 7 أيام من عدم النشاط.',
      },
      {
        heading: '٧. التغييرات على هذه السياسة',
        body: 'قد نحدّث سياسة ملفات تعريف الارتباط هذه من وقت لآخر. سنخطرك بالتغييرات الجوهرية. يُعدّ الاستمرار في استخدام WealthOS بعد التغييرات قبولاً للسياسة المحدّثة.',
      },
      {
        heading: '٨. التواصل',
        body: 'للاستفسار عن استخدامنا لملفات تعريف الارتباط، يرجى التواصل معنا عبر صفحة الدعم أو البريد الإلكتروني support@wealthos.app.',
      },
    ],
  },
  fr: {
    dir: 'ltr' as const,
    back: "Retour à l'accueil",
    badge: 'Politique de cookies',
    title: 'Politique de cookies',
    updated: 'Dernière mise à jour : Août 2026',
    sections: [
      {
        heading: '1. Que sont les cookies',
        body: "Les cookies sont de petits fichiers texte stockés sur votre appareil lorsque vous visitez un site web. Ils aident les sites à mémoriser vos préférences, à vous maintenir connecté et à comprendre comment vous utilisez le site.",
      },
      {
        heading: '2. Comment nous utilisons les cookies',
        body: "WealthOS utilise des cookies pour maintenir votre session (vous garder connecté), mémoriser vos préférences de langue et d'affichage, et analyser l'utilisation de notre plateforme pour améliorer l'expérience.",
      },
      {
        heading: '3. Types de cookies que nous utilisons',
        body: "Cookies nécessaires : Requis pour le bon fonctionnement du site. Ils ne peuvent pas être désactivés et incluent les jetons de session et les cookies d'authentification. Cookies analytiques : Nous aident à comprendre comment les visiteurs interagissent avec WealthOS. Ils ne sont définis qu'avec votre consentement. Cookies marketing : Utilisés pour diffuser du contenu pertinent et mesurer l'efficacité des campagnes. Définis uniquement avec votre consentement explicite.",
      },
      {
        heading: '4. Cookies tiers',
        body: "Nous pouvons utiliser des services tiers tels que des fournisseurs d\'analyse qui définissent leurs propres cookies. Ceux-ci sont soumis aux politiques de confidentialité respectives des tiers. Nous ne contrôlons pas ces cookies.",
      },
      {
        heading: '5. Gestion des cookies',
        body: "Vous pouvez gérer vos préférences de cookies à tout moment via la bannière de consentement affichée lors de votre première visite sur WealthOS. Vous pouvez également contrôler les cookies via les paramètres de votre navigateur. Notez que la désactivation des cookies nécessaires peut affecter les fonctionnalités de la plateforme.",
      },
      {
        heading: '6. Conservation des cookies',
        body: "Les cookies de session sont supprimés lorsque vous fermez votre navigateur. Les cookies persistants restent sur votre appareil pendant une période définie ou jusqu'à ce que vous les supprimiez. Les cookies d'authentification expirent généralement après 7 jours d'inactivité.",
      },
      {
        heading: '7. Modifications de cette politique',
        body: "Nous pouvons mettre à jour cette Politique de cookies de temps en temps. Nous vous informerons des changements importants. L'utilisation continue de WealthOS après les modifications constitue une acceptation de la politique mise à jour.",
      },
      {
        heading: '8. Contact',
        body: "Pour toute question sur notre utilisation des cookies, veuillez nous contacter via la page Support ou par e-mail à support@wealthos.app.",
      },
    ],
  },
};

export default function CookiePolicyPage() {
  const [lang, setLang] = useState<Lang>('en');
  const t = translations[lang];
  const isRtl = t.dir === 'rtl';

  return (
    <div className="min-h-screen bg-background" dir={t.dir}>
      <nav className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/welcome" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} className={isRtl ? 'rotate-180' : ''} />
            {t.back}
          </Link>
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-semibold text-foreground">Wealth<span className="text-primary">OS</span></span>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['en', 'ar', 'fr'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  lang === l ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5">
            <Cookie size={13} className="text-primary" />
            <span className="text-xs font-semibold text-primary">{t.badge}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.updated}</p>
        </div>

        <div className="space-y-6">
          {t.sections.map((section) => (
            <div key={section.heading} className="glass-card rounded-2xl p-6 border border-border">
              <h2 className="text-base font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
          <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
          <Link href="/cookie-policy" className="text-primary font-semibold">Cookie Policy</Link>
          <Link href="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link>
          <Link href="/support" className="hover:text-foreground transition-colors">Support</Link>
        </div>
      </main>
    </div>
  );
}
